import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import Page from "./Page";
import useApi from "../../hooks/useApi";
import usePage from "../../hooks/usePage";

const PAGE_SIZE = 24;
const DEFAULT_IMAGE_FOLDER = "survey-images";
const FILTER_DROPDOWN_MIN_WIDTH = 200;
const FOOTER_METADATA_FIELD_OPTIONS = [
  { id: "projectName", label: "Project" },
  { id: "surveyName", label: "Survey" },
  { id: "pageName", label: "Page" },
  { id: "sectionName", label: "Section" },
  { id: "questionName", label: "Question" },
  { id: "skuName", label: "SKU" },
  { id: "imageNote", label: "Image Notes" },
  { id: "imageType", label: "Image Type" },
  { id: "bannerName", label: "Banner" },
  { id: "retailerName", label: "Retailer" },
  { id: "repId", label: "Rep Name" },
  { id: "locationName", label: "Location Name" },
  { id: "fullAddress", label: "Full Address" },
  { id: "date", label: "Date" },
];

const normalizeImageKey = (imageKey) => {
  if (!imageKey) return null;
  if (typeof imageKey === "string") return imageKey;
  if (typeof imageKey === "object") {
    return (
      imageKey.imageKey ||
      imageKey.fileName ||
      imageKey.data ||
      imageKey.url ||
      imageKey.key ||
      imageKey.name ||
      null
    );
  }
  return String(imageKey);
};

const toStorageRef = (row) => {
  const rawKey = normalizeImageKey(row?.imageKey);
  if (!rawKey)
    return { storageKey: null, folder: null, key: null, rawKey: null };

  if (/^(https?:|data:|blob:)/i.test(rawKey)) {
    try {
      const parsed = new URL(rawKey);
      const pathKey = parsed.pathname.replace(/^\/+/, "");
      if (!pathKey) {
        return { storageKey: rawKey, folder: null, key: null, rawKey };
      }
      const [folder, ...rest] = pathKey.split("/");
      return {
        storageKey: pathKey,
        folder: folder || null,
        key: rest.join("/") || null,
        rawKey,
      };
    } catch {
      return { storageKey: rawKey, folder: null, key: null, rawKey };
    }
  }

  if (rawKey.includes("/")) {
    const [folder, ...rest] = rawKey.split("/");
    return { storageKey: rawKey, folder, key: rest.join("/"), rawKey };
  }

  const folder = row?.imageFolder || row?.folder || DEFAULT_IMAGE_FOLDER;
  return { storageKey: `${folder}/${rawKey}`, folder, key: rawKey, rawKey };
};

const SurveyImages = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { postData, retrieveFile } = useApi();
  const { showMessage } = usePage();
  const userEmail = useSelector((state) => state.authentication?.email);

  const [clientId, setClientId] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [surveyId, setSurveyId] = useState(null);

  const [status, setStatus] = useState("approved");
  const [type, setType] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [pageId, setPageId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [questionId, setQuestionId] = useState("");
  const [retailerId, setRetailerId] = useState("");
  const [bannerId, setBannerId] = useState("");
  const [locationNumber, setLocationNumber] = useState("");
  const [repId, setRepId] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportInfo, setExportInfo] = useState(null);
  const [imageUrls, setImageUrls] = useState({});
  const [hiddenStorageKeys, setHiddenStorageKeys] = useState({});
  const [footerMetadataFields, setFooterMetadataFields] = useState(
    FOOTER_METADATA_FIELD_OPTIONS.map((item) => item.id),
  );
  const [pageOptions, setPageOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [questionOptions, setQuestionOptions] = useState([]);
  const [retailerOptions, setRetailerOptions] = useState([]);
  const [bannerOptions, setBannerOptions] = useState([]);
  const [repOptions, setRepOptions] = useState([]);

  useEffect(() => {
    if (location.state?.surveyId) {
      setSurveyId(location.state.surveyId);
      setProjectId(location.state.projectId);
      setClientId(location.state.clientId);
    } else {
      showMessage("Survey not provided");
      navigate("/survey");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    if (!surveyId) return;
    setPageNumber(1);
    loadImages(1, surveyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    surveyId,
    status,
    type,
    dateRange.startDate,
    dateRange.endDate,
    pageId,
    sectionId,
    questionId,
    retailerId,
    bannerId,
    locationNumber,
    repId,
  ]);

  useEffect(() => {
    if (!surveyId) return;
    loadFilterOptions(surveyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId, pageId, sectionId, retailerId]);

  const buildFilterPayload = (sid = surveyId) => {
    const payload = { surveyId: sid };
    if (status) payload.status = status;
    if (type) payload.type = type;
    if (pageId) payload.pageId = pageId;
    if (sectionId) payload.sectionId = sectionId;
    if (questionId) payload.questionId = questionId;
    if (retailerId) payload.retailerId = retailerId;
    if (bannerId) payload.bannerId = bannerId;
    if (locationNumber) payload.locationNumber = locationNumber;
    if (repId) payload.repId = repId;
    if (dateRange.startDate && dateRange.endDate) {
      payload.dateRange = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
    }
    return payload;
  };

  const loadFilterOptions = async (sid = surveyId) => {
    if (!sid) return;

    const response = await postData({
      entityName: "SurveyAnswer",
      action: "getImageFilterOptions",
      data: {
        surveyId: sid,
        pageId: pageId || null,
        sectionId: sectionId || null,
        retailerId: retailerId || null,
      },
    });

    if (!response?.isSuccessful) {
      setPageOptions([]);
      setSectionOptions([]);
      setQuestionOptions([]);
      setRetailerOptions([]);
      setBannerOptions([]);
      setRepOptions([]);
      return;
    }

    const options = response.data || {};
    const pages = Array.isArray(options.pages) ? options.pages : [];
    const sections = Array.isArray(options.sections) ? options.sections : [];
    const questions = Array.isArray(options.questions) ? options.questions : [];
    const retailers = Array.isArray(options.retailers) ? options.retailers : [];
    const banners = Array.isArray(options.banners) ? options.banners : [];
    const reps = Array.isArray(options.reps) ? options.reps : [];

    setPageOptions(pages);
    setSectionOptions(sections);
    setQuestionOptions(questions);
    setRetailerOptions(retailers);
    setBannerOptions(banners);
    setRepOptions(reps);

    if (pageId && !pages.some((item) => item.id === pageId)) {
      setPageId("");
    }
    if (sectionId && !sections.some((item) => item.id === sectionId)) {
      setSectionId("");
    }
    if (questionId && !questions.some((item) => item.id === questionId)) {
      setQuestionId("");
    }
    if (retailerId && !retailers.some((item) => item.id === retailerId)) {
      setRetailerId("");
    }
    if (bannerId && !banners.some((item) => item.id === bannerId)) {
      setBannerId("");
    }
    if (repId && !reps.some((item) => item.id === repId)) {
      setRepId("");
    }
  };

  const loadImages = async (page = pageNumber, sid = surveyId) => {
    if (!sid) return;
    setLoading(true);
    const response = await postData({
      entityName: "SurveyAnswer",
      action: "getImagesWithMetadata",
      data: {
        filter: buildFilterPayload(sid),
        pageNumber: page,
        recordsPerPage: PAGE_SIZE,
      },
    });

    const rows = Array.isArray(response?.data) ? response.data : [];
    setImages(rows);
    setHiddenStorageKeys({});
    await hydrateUrls(rows);
    setLoading(false);
  };

  const hydrateUrls = async (rows) => {
    const pending = rows
      .map((row) => ({ row, ref: toStorageRef(row) }))
      .filter(({ ref }) => ref.storageKey && !(ref.storageKey in imageUrls));

    if (pending.length === 0) return;

    const fetches = pending.map(async ({ row, ref }) => {
      try {
        const url =
          ref.folder && ref.key
            ? await retrieveFile(ref.folder, ref.key)
            : await retrieveFile(ref.storageKey);
        return { storageKey: ref.storageKey, url };
      } catch (err) {
        console.error(
          "Image retrieve failed",
          { storageKey: ref.storageKey, row },
          err?.message || err,
        );
        return { storageKey: ref.storageKey, url: null };
      }
    });

    const results = await Promise.all(fetches);
    setImageUrls((prev) => {
      const next = { ...prev };
      for (const entry of results) {
        next[entry.storageKey] = entry.url || null;
      }
      return next;
    });
  };

  const handleExport = async () => {
    if (!clientId || !surveyId) {
      showMessage("Missing survey context for export.");
      return;
    }
    setExporting(true);
    const res = await postData({
      entityName: "ImageExportGallery",
      action: "generate",
      data: {
        clientId,
        surveyIds: [surveyId],
        pageIds: pageId ? [pageId] : [],
        sectionIds: sectionId ? [sectionId] : [],
        questionIds: questionId ? [questionId] : [],
        projectIds: projectId ? [projectId] : [],
        retailerIds: retailerId ? [retailerId] : [],
        bannerIds: bannerId ? [bannerId] : [],
        locationNumbers: locationNumber ? [locationNumber] : [],
        repIds: repId ? [repId] : [],
        dateRange:
          dateRange.startDate && dateRange.endDate
            ? { startDate: dateRange.startDate, endDate: dateRange.endDate }
            : null,
        status: status || "",
        type: type || "",
        email: userEmail,
        metadataFields: footerMetadataFields,
      },
    });
    setExporting(false);
    if (res?.isSuccessful) {
      setExportInfo(res.data || res);
      showMessage("Gallery export queued");
    } else {
      showMessage(res?.message || "Failed to queue export");
    }
  };

  const renderImageCard = (row, index) => {
    const { storageKey } = toStorageRef(row);
    if (!storageKey) return null;

    if (hiddenStorageKeys[storageKey]) return null;

    const url = storageKey ? imageUrls[storageKey] : null;
    if (url === null) return null;

    const qaStatus = row.approval?.status || "pending";
    const qaStatusColor =
      qaStatus === "approved"
        ? "success"
        : qaStatus === "unapproved"
          ? "error"
          : "warning";
    const metadata = row.metadata || {};
    const hasMeta = (field) => footerMetadataFields.includes(field);
    const cityProv = [metadata.locationCity, metadata.locationProvince]
      .filter(Boolean)
      .join(", ");
    const locationNameDisplay = String(metadata.locationName || "").trim();
    const locationNumberDisplay = String(metadata.locationNumber || "").trim();
    const streetAddressLine = [
      metadata.address?.streetAddress,
      metadata.address?.otherAddress,
      metadata.address?.line1,
      metadata.address?.line2,
    ]
      .filter(Boolean)
      .join(", ");
    const cityProvincePostalLine = [
      metadata.locationCity || metadata.address?.city,
      metadata.locationProvince || metadata.address?.province,
      metadata.address?.postal || metadata.address?.postalCode,
    ]
      .filter(Boolean)
      .join(", ");
    const schedule = metadata.scheduleDate
      ? new Date(metadata.scheduleDate).toLocaleDateString()
      : "";
    return (
      <Card
        key={`${storageKey || "image"}-${index}`}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            backgroundColor: "#111",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 220,
          }}
        >
          {url ? (
            <img
              src={url}
              alt={row.metadata?.questionName || "image"}
              loading="lazy"
              onError={() => {
                setHiddenStorageKeys((prev) => ({
                  ...prev,
                  [storageKey]: true,
                }));
              }}
              style={{
                maxWidth: "100%",
                maxHeight: "220px",
                objectFit: "contain",
              }}
            />
          ) : (
            <CircularProgress size={24} />
          )}
        </Box>
        <CardContent
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            {hasMeta("imageType") && (
              <Chip
                label={row.imageType}
                size="small"
              />
            )}
            <Chip
              label={qaStatus}
              color={qaStatusColor}
              size="small"
            />
          </Stack>
          {hasMeta("projectName") && (
            <Typography variant="subtitle2">{metadata.projectName}</Typography>
          )}
          {hasMeta("surveyName") && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.surveyName}
            </Typography>
          )}
          {hasMeta("pageName") && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.pageName}
            </Typography>
          )}
          {hasMeta("sectionName") && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.sectionName}
            </Typography>
          )}
          {hasMeta("questionName") && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.questionName}
            </Typography>
          )}
          {hasMeta("skuName") && metadata.skuName && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.skuName}
            </Typography>
          )}
          {hasMeta("imageNote") && metadata.imageNote && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.imageNote}
            </Typography>
          )}
          {hasMeta("retailerName") && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.retailerName}
            </Typography>
          )}
          {hasMeta("bannerName") && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.bannerName}
            </Typography>
          )}
          {hasMeta("repId") && (metadata.repName || metadata.repId) && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {String(metadata.repName || metadata.repId)}
            </Typography>
          )}
          {hasMeta("locationName") &&
            (locationNameDisplay || locationNumberDisplay) && (
              <>
                {locationNameDisplay && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {locationNameDisplay}
                  </Typography>
                )}
                {locationNumberDisplay && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    #{locationNumberDisplay}
                  </Typography>
                )}
              </>
            )}
          {hasMeta("fullAddress") &&
            (streetAddressLine || cityProvincePostalLine) && (
              <>
                {streetAddressLine && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {streetAddressLine}
                  </Typography>
                )}
                {cityProvincePostalLine && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {cityProvincePostalLine}
                  </Typography>
                )}
              </>
            )}
          {!hasMeta("fullAddress") && cityProv && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {cityProv}
            </Typography>
          )}
          {hasMeta("date") && schedule && (
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {schedule}
            </Typography>
          )}
          <Box sx={{ mt: "auto", display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              href={url || "#"}
              target="_blank"
              rel="noreferrer"
              disabled={!url}
            >
              View
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Page
      pageName="surveyImages"
      includeToolbar
      items={[{ name: "survey", iconName: "Assessment" }]}
      onToolItemClick={(item) => {
        if (item === "survey") {
          navigate("/survey", { state: { surveyId, projectId, clientId } });
        }
      }}
    >
      <Stack spacing={2}>
        {/* Filters Section - Full Width */}
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ mb: 2 }}
            >
              Filters
            </Typography>
            <Grid
              container
              spacing={2}
              alignItems="center"
            >
              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <TextField
                  label="Start date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={dateRange.startDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <TextField
                  label="End date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={dateRange.endDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}
                >
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="unapproved">Unapproved</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}
                >
                  <InputLabel id="type-select-label">Image Type</InputLabel>
                  <Select
                    labelId="type-select-label"
                    label="Image Type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="image">Standard</MenuItem>
                    <MenuItem value="beforeAfter">Before/After</MenuItem>
                    <MenuItem value="distribution">Distribution</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}
                >
                  <InputLabel id="page-select-label">Page</InputLabel>
                  <Select
                    labelId="page-select-label"
                    label="Page"
                    value={pageId}
                    onChange={(e) => setPageId(e.target.value)}
                  >
                    <MenuItem value="">All Pages</MenuItem>
                    {pageOptions.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}
                >
                  <InputLabel id="section-select-label">Section</InputLabel>
                  <Select
                    labelId="section-select-label"
                    label="Section"
                    value={sectionId}
                    onChange={(e) => setSectionId(e.target.value)}
                  >
                    <MenuItem value="">All Sections</MenuItem>
                    {sectionOptions.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}
                >
                  <InputLabel id="question-select-label">Question</InputLabel>
                  <Select
                    labelId="question-select-label"
                    label="Question"
                    value={questionId}
                    onChange={(e) => setQuestionId(e.target.value)}
                  >
                    <MenuItem value="">All Questions</MenuItem>
                    {questionOptions.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}
                >
                  <InputLabel id="retailer-select-label">Retailer</InputLabel>
                  <Select
                    labelId="retailer-select-label"
                    label="Retailer"
                    value={retailerId}
                    onChange={(e) => setRetailerId(e.target.value)}
                  >
                    <MenuItem value="">All Retailers</MenuItem>
                    {retailerOptions.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}
                >
                  <InputLabel id="banner-select-label">Banner</InputLabel>
                  <Select
                    labelId="banner-select-label"
                    label="Banner"
                    value={bannerId}
                    onChange={(e) => setBannerId(e.target.value)}
                  >
                    <MenuItem value="">All Banners</MenuItem>
                    {bannerOptions.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <TextField
                  label="Location Number"
                  size="small"
                  fullWidth
                  value={locationNumber}
                  onChange={(e) => setLocationNumber(e.target.value)}
                />
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}
                >
                  <InputLabel id="rep-select-label">Rep</InputLabel>
                  <Select
                    labelId="rep-select-label"
                    label="Rep"
                    value={repId}
                    onChange={(e) => setRepId(e.target.value)}
                  >
                    <MenuItem value="">All Reps</MenuItem>
                    {repOptions.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!surveyId || loading}
                  onClick={() => {
                    setPageNumber(1);
                    loadImages(1);
                  }}
                >
                  {loading ? "Loading" : "Apply"}
                </Button>
              </Grid>

              <Grid
                xs={12}
                sm={6}
                md={2}
              >
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setStatus("approved");
                    setType("");
                    setDateRange({ startDate: "", endDate: "" });
                    setPageId("");
                    setSectionId("");
                    setQuestionId("");
                    setRetailerId("");
                    setBannerId("");
                    setLocationNumber("");
                    setRepId("");
                    setImages([]);
                    setImageUrls({});
                  }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Stack
              direction="row"
              spacing={2}
            >
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1 }}
                >
                  Footer Metadata
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Default is all metadata. If none is selected, no footer will
                  be added to exported images.
                </Typography>
                <FormGroup row>
                  {FOOTER_METADATA_FIELD_OPTIONS.map((item) => {
                    const checked = footerMetadataFields.includes(item.id);
                    return (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFooterMetadataFields((prev) => {
                                if (isChecked) {
                                  return Array.from(
                                    new Set([...prev, item.id]),
                                  );
                                }
                                return prev.filter(
                                  (field) => field !== item.id,
                                );
                              });
                            }}
                          />
                        }
                        label={item.label}
                      />
                    );
                  })}
                </FormGroup>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={2}
            >
              <Button
                variant="outlined"
                disabled={!clientId || !surveyId || exporting}
                onClick={handleExport}
              >
                {exporting ? "Queuing..." : "Export Gallery"}
              </Button>
              {exportInfo && (
                <Box>
                  <Typography
                    variant="body2"
                    color="success.main"
                  >
                    Gallery queued - you will receive an email with the download
                    link
                  </Typography>
                  {exportInfo.expiresAt && (
                    <Typography variant="caption">
                      Link expires:{" "}
                      {new Date(exportInfo.expiresAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Images Section - Full Width */}
        <Box>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="h6">Images</Typography>
            <Chip
              label={`${images.length} items`}
              size="small"
            />
          </Stack>
          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading images…</Typography>
            </Box>
          )}
          {!loading && images.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              No images found for the current filters.
            </Typography>
          )}
          <Grid
            container
            spacing={2}
          >
            {images.map((row, idx) => {
              const { storageKey } = toStorageRef(row);
              return (
                <Grid
                  key={`${storageKey || "image"}-${idx}`}
                  xs={12}
                  sm={6}
                  md={4}
                >
                  {renderImageCard(row, idx)}
                </Grid>
              );
            })}
          </Grid>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
          >
            <Button
              variant="outlined"
              disabled={pageNumber === 1 || loading}
              onClick={() => {
                const next = Math.max(1, pageNumber - 1);
                setPageNumber(next);
                loadImages(next);
              }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              disabled={images.length < PAGE_SIZE || loading}
              onClick={() => {
                const next = pageNumber + 1;
                setPageNumber(next);
                loadImages(next);
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Stack>
    </Page>
  );
};

export default SurveyImages;
